import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plane, Check, ShieldCheck, Lock, Loader2, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { PLAN_DETAILS } from "@/components/landing/Pricing";
import { track, EVENTS } from "@/lib/analytics";

const TRUST = ["Secure payment", "Personalized AI coach", "Adaptive preparation", "Performance tracking", "Support"];

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Checkout() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [planId, setPlanId] = useState(sessionStorage.getItem("rp_plan") || "annual");
  const [config, setConfig] = useState(null); // { enabled, key_id }
  const [cardName, setCardName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const plan = PLAN_DETAILS[planId] || PLAN_DETAILS.annual;

  useEffect(() => {
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => setConfig({ enabled: false }));
  }, []);

  useEffect(() => {
    track(EVENTS.CHECKOUT_STARTED, { plan: planId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  const onSuccess = async () => {
    track(EVENTS.PAYMENT_SUCCESS, { plan: planId });
    await refreshUser();
    sessionStorage.removeItem("rp_plan");
    navigate("/onboarding");
  };

  const payWithRazorpay = async () => {
    setError("");
    setLoading(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Couldn't load the payment gateway. Check your connection and retry.");
      const { data: order } = await api.post("/payments/order", { plan: planId });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "sarkarijobai",
        description: `${plan.name} plan`,
        prefill: { name: order.name || user?.name || "", email: order.email || user?.email || "" },
        theme: { color: "#ef4444" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              plan: planId,
            });
            await onSuccess();
          } catch (err) {
            logger.error("verify failed:", err?.message);
            setError("Payment could not be verified. If you were charged, contact support.");
            setLoading(false);
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.on("payment.failed", () => {
        setError("Payment failed. Please try again.");
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const payMock = async () => {
    setError("");
    setLoading(true);
    try {
      // NOTE: Fallback DEMO path (used only while Razorpay keys are not configured).
      await api.post("/checkout/confirm", { plan: planId, card_name: cardName });
      await onSuccess();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onPay = (e) => {
    e.preventDefault();
    if (config?.enabled) payWithRazorpay();
    else payMock();
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-amber-400 to-red-600 flex items-center justify-center">
              <Plane className="h-4 w-4 text-white -rotate-45" />
            </div>
            <span className="font-display text-lg font-bold">sarkarijobai</span>
          </Link>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" /> Secure checkout</span>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="checkout-back">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="grid lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
          {/* LEFT: plan summary */}
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight mb-1">
              You're one step away from a better preparation system.
            </h1>
            <p className="text-sm text-muted-foreground mb-6">Choose your plan and unlock the full sarkarijobai experience.</p>

            <div className="flex gap-2 mb-6" data-testid="checkout-plan-switcher">
              {Object.values(PLAN_DETAILS).map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPlanId(p.id)}
                  className={`flex-1 rounded-xl border px-3 py-3 text-left transition-colors ${
                    planId === p.id ? "border-primary bg-primary/5" : "border-border"
                  }`}
                  data-testid={`checkout-plan-${p.id}`}
                >
                  <span className={`block text-xs font-semibold uppercase tracking-wide ${planId === p.id ? "text-primary" : "text-muted-foreground"}`}>{p.name}</span>
                  <span className="block text-sm font-bold mt-0.5">{p.price}<span className="text-xs font-normal text-muted-foreground">{p.period}</span></span>
                </button>
              ))}
            </div>

            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="flex items-baseline justify-between">
                <span className="font-display text-sm font-bold uppercase tracking-wide">{plan.name}</span>
                <span className="font-display text-2xl font-bold">{plan.price}<span className="text-sm text-muted-foreground font-normal">{plan.period}</span></span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{plan.tagline}</p>
              <ul className="mt-5 space-y-2.5">
                {TRUST.map((t) => (
                  <li key={t} className="flex items-center gap-2.5 text-sm">
                    <Check className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-muted-foreground">{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-5 rounded-2xl border border-amber-400/30 bg-amber-500/[0.06] p-5">
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-foreground leading-relaxed">
                  Your selected plan may qualify for the sarkarijobai Success Guarantee, subject to eligibility terms.
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT: payment */}
          <div>
            <div className="rounded-2xl border border-border bg-card p-6 lg:p-8">
              <h2 className="font-display text-lg font-bold mb-1">Payment</h2>
              {config?.enabled ? (
                <p className="text-xs text-muted-foreground mb-6">Pay securely via Razorpay — UPI, cards, netbanking & wallets.</p>
              ) : (
                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mb-6">Demo mode — Razorpay keys not configured yet, no real payment is processed.</p>
              )}

              <form onSubmit={onPay} className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Full name</label>
                  <input type="text" required value={cardName} onChange={(e) => setCardName(e.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Aarav Sharma" data-testid="checkout-card-name" />
                </div>

                {config?.enabled ? (
                  <div className="rounded-xl border border-border bg-muted/40 p-4 flex items-center gap-3">
                    <img src="https://razorpay.com/favicon.png" alt="" className="h-6 w-6 rounded" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    <p className="text-xs text-muted-foreground">You'll complete payment in a secure Razorpay window. Card details are never stored on our servers.</p>
                  </div>
                ) : null}

                {error && <p className="text-sm text-destructive" data-testid="checkout-error">{error}</p>}

                <button type="submit" disabled={loading || config === null}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-red-600 text-white font-medium py-3.5 hover:scale-[1.02] transition-transform disabled:opacity-60"
                  data-testid="checkout-pay-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4" /> Pay {plan.price}</>}
                </button>
                <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
                  <Lock className="h-3 w-3" /> Payments are encrypted and secure
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
